/**
 * Created by Ning Jiang on 2021-06-18.
 */

import {gsap, TimelineLite} from "gsap";
import {MathHelper} from "../../../../core/utils/MathHelper";
import {DeadwoodRipGameAssets} from "../../../DeadwoodRipGameAssets";
import {DeadwoodRipLayers, Layers} from "../../../view/DeadwoodRipLayers";
import {DeadwoodRipSound} from "../../../sound/DeadwoodRipSound";
import {DeadwoodRip} from "../../../DeadwoodRip";
import {BitmapCountUp} from "../../../../core/component/BitmapCountUp";
import {ICountUp} from "../../ICountUp";

export class InitialWinCountUp extends PIXI.Container implements ICountUp {

    protected _countUp:BitmapCountUp;
    private _endingScale:number = 0.6;
    private _isSkip:boolean = false;
    private _isCountUpSoundPlaying:boolean = false;

    public set externalAlpha(value:number) {
        this._countUp.alpha = value;
    }

    public get externalAlpha():number {
        return this._countUp.alpha;
    }

    constructor() {
        super();

        const style = {
            fontName : DeadwoodRipGameAssets.BITMAP_FONT_WIN_COUNT_UP,
            fontSize : 60,
            tint : parseInt(("#ffffff").replace(/^#/, ''), 16)
        };
        this._countUp = new BitmapCountUp(style);
        this._countUp.letterSpacing = -30;
        (<PIXI.Point>this._countUp.anchor).y = 0.82;
        this._countUp.shrinkSize = true;
        this._countUp.visible = false;
        this.addChild(this._countUp);
        this.position.set(360, 360);
        DeadwoodRipLayers.getLayer(Layers.WIN_COUNT_UP)?.addChild(this);
    }

    private getScale(multiplier:number):number {
        return 1 + (Math.min(multiplier, 6) - 1) * 0.1;
    }

    public getCountUpAnimation(from:number, to:number, duration:number, multiplier:number):TimelineLite {
        this._isSkip = false;
        multiplier = multiplier === 0 ? 1 : multiplier;
        const baseToNumber = multiplier === 1 ? to : MathHelper.roundToDecimals(to / multiplier, 2);
        const tl = new TimelineLite();
        tl.add([
            () => {
                this._countUp.maxTextWidth = 600 / this.getScale(multiplier);
                this._countUp.scale.set(1);
                this._countUp.alpha = 1;
                this._countUp.visible = true;
                if(duration > 0.01) {
                    this._isCountUpSoundPlaying = true;
                    this.loopCountUpSound();
                }
            },
            this._countUp.getCountUpAnimation(from, baseToNumber, duration, `power3.inOut`)
        ]);

        tl.add(() => {
            this.stopCountUpSound();
        });

        if(multiplier > 1) {
            let levelTime = 0.5;
            for(let m:number = 1; m <= multiplier; m++) {
                levelTime = Math.max(levelTime * 0.9, 0.1);
                const showNumber:number = m === multiplier ? to : (baseToNumber * m);
                const scale:number = this.getScale(m);
                tl.add([
                    () => {
                        if(!this._isSkip) {
                            DeadwoodRipSound.soundPlayer.playTrack({
                                name : DeadwoodRipSound.COUNT_UP_MULTIPLIER,
                                volume : 1
                            });
                        }
                    },
                    this._countUp.getCountUpAnimation(showNumber, showNumber, 0.01),
                    gsap.to(this._countUp.scale,
                        {
                            duration : levelTime * 0.5,
                            x : scale,
                            y : scale,
                            ease : `elastic.out(2, 1)`
                        }),
                    gsap.to(this, {duration : levelTime}) // a pause for half levelTime
                ]);
            }
        }

        // ending stay
        tl.add(() => {this.stay(multiplier);});

        return tl;
    }

    private stay(multiplier:number):TimelineLite {
        this._isSkip = false;
        const tl = new TimelineLite();
        tl.add(() => {DeadwoodRipSound.soundPlayer.playTrack({name : this.getCountUpEndSoundName()})});
        tl.add(gsap.to(this._countUp.scale, {
            duration : 0.5,
            x : this.getScale(multiplier) * 1.05,
            y : this.getScale(multiplier) * 1.05,
            ease : `elastic.out(2, 0.75)`
        }));
        tl.add([
            gsap.to(this._countUp.scale, {
                duration : 0.5,
                x : this._endingScale,
                y : this._endingScale,
                ease : `elastic.out(1, 1)`
                // ease: Power4.easeOut
            }),
        ]);
        // timeline.add(() => ElPasoGunfightSoundConfig.soundPlayer.playTrack({name : ElPasoGunfightSoundConfig.instance.COUNT_UP_END_FALL_BACK}), "-=0.3");
        return tl;
    }

    public remove():TimelineLite {
        const tl = new TimelineLite();
        if(this._countUp.visible) {
            tl.add(gsap.to(this._countUp, {duration : 0.5, alpha : 0, y : -100, ease : `power2.in`}), `+=0.2`); // stay a short while after count up.
            tl.add(() => {
                this._countUp.y = 0;
                this._countUp.visible = false;
            });
        }

        return tl;
    }

    public showAfterRemoved():void {
        this._countUp.visible = true;
    }

    private loopCountUpSound():void {
        if(!this._isCountUpSoundPlaying) {
            return;
        }

        DeadwoodRipSound.soundPlayer.playTrack({
            name : DeadwoodRipSound.COUNT_UP_COUNTER,
            repeat : 1000
        });
    }

    private stopCountUpSound():void {
        if(this._isCountUpSoundPlaying) {
            DeadwoodRipSound.fadeAndStopSound({
                name : DeadwoodRipSound.COUNT_UP_COUNTER,
                to : 0,
                duration : 100
            });
            this._isCountUpSoundPlaying = false;
        }
    }

    private getCountUpEndSoundName():string {
        switch(DeadwoodRip.model.parsedGameData.currentWinRatioConfig!.winLevel) {
            case 0:
            case 1:
                return DeadwoodRipSound.COUNT_UP_END_SMALL;
            case 2:
                return DeadwoodRipSound.COUNT_UP_END_MEDIUM;
            case 3:
            default:
                return DeadwoodRipSound.COUNT_UP_END_LARGE;
        }
    }

    public skip():void {
        this._isSkip = true;
    }
}